import { useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { Button, InputText, ModalDefault, Notice, Text } from "@ficatranquilo/contabilidade.com-ui";
import useService from "../../hooks/useService";
import { escapeHtml } from "../ow";

const CONFIRM_WORD = "REMOVER";

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

/**
 * Remoção de um colaborador da empresa ativa: na lista de colaboradores e no
 * detalhe. O back só desfaz o vínculo (o histórico fica e um novo convite
 * reativa), mas a pessoa perde o acesso: o botão só libera depois de digitar
 * REMOVER.
 *
 * Props:
 * - employee: colaborador ({ id, name })
 * - activeContracts: contratos ativos ou pausados, quando quem abre já sabe (o
 *   detalhe). Com algum, o modal avisa e não deixa remover, como o back faria
 * - onClose
 * - onRemoved: depois de remover (recarregar a lista ou voltar para ela)
 */
export default function RemoveEmployeeModal({ employee, activeContracts = 0, onClose, onRemoved }) {
  const employeeService = useService("users");
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);

  const blocked = activeContracts > 0;
  const confirmed = confirmText.trim().toUpperCase() === CONFIRM_WORD;

  function close() {
    if (!loading) onClose();
  }

  async function confirm() {
    if (!confirmed || blocked) return;
    setLoading(true);
    try {
      await employeeService.delete(employee.id);
      toast.success("Colaborador removido com sucesso!");
      setLoading(false);
      onRemoved();
    } catch (err) {
      // Com contrato ativo o back recusa; o modal fica aberto
      toast.error(err?.response?.data?.msg || err?.message || "Erro ao remover colaborador");
      setLoading(false);
    }
  }

  return (
    <ModalDefault
      $showModal
      $setShowModal={close}
      $noClose={loading}
      $size="medium"
      $title="Remover colaborador"
      $contentBackground="N0"
      $footer={
        <>
          <Button $text="Cancelar" $variant="outline" $action={close} $isDisabled={loading} $noWidth />
          <Button
            $text="Remover colaborador"
            $variant="danger"
            $action={confirm}
            $isDisabled={!confirmed || blocked}
            $isLoading={loading}
            $noWidth
          />
        </>
      }
    >
      <Content>
        <Text
          $text="<strong>%s</strong> perde o acesso a esta empresa. O histórico (contratos encerrados, notas fiscais, horas) é mantido e um novo convite reativa o vínculo."
          $replaces={[escapeHtml(employee.name || "O colaborador")]}
          $variant="paragraph_small_medium"
          $color="N700"
          $colorEmphasis="N900"
        />
        {blocked ? (
          <Notice
            $variant="orange"
            $text={
              activeContracts === 1
                ? "Este colaborador tem 1 contrato ativo ou pausado. Encerre o contrato antes de remover."
                : "Este colaborador tem %s contratos ativos ou pausados. Encerre os contratos antes de remover."
            }
            $replaces={[String(activeContracts)]}
          />
        ) : (
          <InputText
            $idForm="remove-employee-confirm"
            $label={`Digite ${CONFIRM_WORD} para confirmar`}
            $variant="default"
            autoComplete="off"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") confirm();
            }}
          />
        )}
      </Content>
    </ModalDefault>
  );
}
